export interface PermissionDebugEntry {
  id: string
  pathname: string
  systemCode: string
  permission?: string
  permissionAllowed: boolean
  allowed: boolean
  mode: 'hide' | 'disable'
  action?: string
  location?: string
  sourceLocation?: string
  source: 'permission' | 'explicit'
}

function createPermissionDebugStore() {
  const entries = new Map<string, PermissionDebugEntry>()
  const permissionOverrides = new Map<string, boolean>()
  const listeners = new Set<() => void>()
  let snapshot: PermissionDebugEntry[] = []

  const permissionKey = (systemCode: string, permission: string) => JSON.stringify([systemCode, permission])

  const applyPermissionOverride = (entry: PermissionDebugEntry): PermissionDebugEntry => {
    if (entry.source !== 'permission' || !entry.permission) return entry

    const override = permissionOverrides.get(permissionKey(entry.systemCode, entry.permission))
    return {
      ...entry,
      allowed: override ?? entry.permissionAllowed,
    }
  }

  const emit = () => {
    snapshot = Array.from(entries.values())
    listeners.forEach((listener) => listener())
  }

  const unregister = (id: string) => {
    entries.delete(id)
    emit()
  }

  return {
    clear() {
      entries.clear()
      permissionOverrides.clear()
      emit()
    },
    getEntries() {
      return snapshot
    },
    register(entry: PermissionDebugEntry) {
      entries.set(entry.id, applyPermissionOverride(entry))
      emit()

      return () => {
        unregister(entry.id)
      }
    },
    unregister,
    getPermissionOverride(systemCode: string, permission: string) {
      return permissionOverrides.get(permissionKey(systemCode, permission))
    },
    hasPermissionOverrides() {
      return permissionOverrides.size > 0
    },
    setPermissionOverride(systemCode: string, permission: string, allowed: boolean) {
      permissionOverrides.set(permissionKey(systemCode, permission), allowed)
      entries.forEach((entry, id) => {
        entries.set(id, applyPermissionOverride(entry))
      })
      emit()
    },
    resetPermissionOverrides() {
      permissionOverrides.clear()
      entries.forEach((entry, id) => {
        entries.set(id, applyPermissionOverride(entry))
      })
      emit()
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

export const permissionDebugStore = createPermissionDebugStore()
